export type LuminosityMode = 'system' | 'light' | 'dark';

export type BrandPreset = {
  id: string;
  label: string;
  description?: string;
  luminosityDefault: Exclude<LuminosityMode, 'system'>;
  tokens: {
    brandPrimary: string;
    brandSecondary?: string;
    brandHighlight?: string;
    brandWarmth?: string;
    uiBg: string;
    uiBgElevated: string;
    uiSurface: string;
    uiSurface2: string;
    uiBorder: string;
    uiBorderStrong: string;
    uiText: string;
    uiTextMuted: string;
    uiTextSubtle: string;
    uiTextInverse: string;
    uiAccent: string;
    uiAccentHover: string;
    uiAccentSoft: string;
    uiFocus: string;
    uiSuccess: string;
    uiWarning: string;
    uiDanger: string;
    shadowRgb: string;
  };
  typography: {
    display: string;
    body: string;
    mono?: string;
  };
  shape: {
    radiusSm: string;
    radiusMd: string;
    radiusLg: string;
  };
  density: {
    compact: string;
    regular: string;
    spacious: string;
  };
};

export const brandPresets: BrandPreset[] = [
  {
    id: 'ember',
    label: 'Ember',
    description: 'Base cálida y contrastada para la transición del panel.',
    luminosityDefault: 'light',
    tokens: {
      brandPrimary: '170 82 45',
      brandSecondary: '113 138 87',
      brandHighlight: '227 179 108',
      brandWarmth: '244 232 213',
      uiBg: '250 244 236',
      uiBgElevated: '255 250 245',
      uiSurface: '255 250 245',
      uiSurface2: '245 235 223',
      uiBorder: '225 210 190',
      uiBorderStrong: '195 171 144',
      uiText: '52 33 20',
      uiTextMuted: '97 72 53',
      uiTextSubtle: '133 104 83',
      uiTextInverse: '255 248 240',
      uiAccent: '170 82 45',
      uiAccentHover: '144 65 34',
      uiAccentSoft: '170 82 45 / 0.14',
      uiFocus: '193 110 72',
      uiSuccess: '59 130 91',
      uiWarning: '191 120 47',
      uiDanger: '179 68 58',
      shadowRgb: '88 52 31',
    },
    typography: {
      display: '"Fraunces", "Georgia", serif',
      body: '"Manrope", "Segoe UI", sans-serif',
      mono: '"IBM Plex Mono", monospace',
    },
    shape: {
      radiusSm: '8px',
      radiusMd: '14px',
      radiusLg: '22px',
    },
    density: {
      compact: '0.92',
      regular: '1',
      spacious: '1.08',
    },
  },
  {
    id: 'rio',
    label: 'Rio',
    description: 'Preset interno más sobrio para futuras variantes.',
    luminosityDefault: 'light',
    tokens: {
      brandPrimary: '33 84 95',
      brandSecondary: '158 100 69',
      brandHighlight: '76 135 145',
      brandWarmth: '236 234 228',
      uiBg: '241 241 237',
      uiBgElevated: '250 250 247',
      uiSurface: '252 252 248',
      uiSurface2: '233 233 227',
      uiBorder: '205 210 206',
      uiBorderStrong: '166 174 171',
      uiText: '29 39 43',
      uiTextMuted: '73 87 92',
      uiTextSubtle: '104 117 122',
      uiTextInverse: '244 247 248',
      uiAccent: '33 84 95',
      uiAccentHover: '24 67 77',
      uiAccentSoft: '33 84 95 / 0.14',
      uiFocus: '76 135 145',
      uiSuccess: '52 127 94',
      uiWarning: '187 126 47',
      uiDanger: '178 69 68',
      shadowRgb: '32 47 55',
    },
    typography: {
      display: '"Marcellus", "Georgia", serif',
      body: '"Mulish", "Segoe UI", sans-serif',
      mono: '"IBM Plex Mono", monospace',
    },
    shape: {
      radiusSm: '8px',
      radiusMd: '14px',
      radiusLg: '22px',
    },
    density: {
      compact: '0.92',
      regular: '1',
      spacious: '1.08',
    },
  },
  {
    id: 'iris',
    label: 'Iris',
    description: 'Preset interno frío de acento índigo, más técnico y sobrio.',
    luminosityDefault: 'light',
    tokens: {
      brandPrimary: '79 70 229',
      brandSecondary: '124 110 230',
      brandHighlight: '167 139 250',
      brandWarmth: '237 233 254',
      uiBg: '244 244 251',
      uiBgElevated: '251 251 255',
      uiSurface: '252 252 255',
      uiSurface2: '236 235 248',
      uiBorder: '216 214 235',
      uiBorderStrong: '181 176 216',
      uiText: '30 27 56',
      uiTextMuted: '74 70 110',
      uiTextSubtle: '111 106 144',
      uiTextInverse: '247 246 255',
      uiAccent: '79 70 229',
      uiAccentHover: '67 56 202',
      uiAccentSoft: '79 70 229 / 0.14',
      uiFocus: '124 110 230',
      uiSuccess: '52 127 94',
      uiWarning: '187 126 47',
      uiDanger: '190 64 72',
      shadowRgb: '40 35 80',
    },
    typography: {
      display: '"Sora", "Segoe UI", sans-serif',
      body: '"Sora", "Segoe UI", sans-serif',
      mono: '"IBM Plex Mono", monospace',
    },
    shape: {
      radiusSm: '8px',
      radiusMd: '14px',
      radiusLg: '22px',
    },
    density: {
      compact: '0.92',
      regular: '1',
      spacious: '1.08',
    },
  },
  {
    id: 'clasico',
    label: 'Clásico',
    description: 'Identidad azul corporativa original del panel.',
    luminosityDefault: 'light',
    tokens: {
      brandPrimary: '1 88 173',
      brandSecondary: '71 85 105',
      brandHighlight: '52 121 190',
      brandWarmth: '241 245 249',
      uiBg: '248 250 252',
      uiBgElevated: '255 255 255',
      uiSurface: '255 255 255',
      uiSurface2: '241 245 249',
      uiBorder: '226 232 240',
      uiBorderStrong: '203 213 225',
      uiText: '15 23 42',
      uiTextMuted: '71 85 105',
      uiTextSubtle: '100 116 139',
      uiTextInverse: '248 250 252',
      uiAccent: '1 88 173',
      uiAccentHover: '0 68 147',
      uiAccentSoft: '1 88 173 / 0.14',
      uiFocus: '52 121 190',
      uiSuccess: '52 127 94',
      uiWarning: '187 126 47',
      uiDanger: '178 69 68',
      shadowRgb: '15 23 42',
    },
    typography: {
      display: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      body: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      mono: '"IBM Plex Mono", monospace',
    },
    shape: {
      radiusSm: '8px',
      radiusMd: '14px',
      radiusLg: '22px',
    },
    density: {
      compact: '0.92',
      regular: '1',
      spacious: '1.08',
    },
  },
];

export const defaultBrandPresetId = 'iris';

export function isBrandPresetId(value: string | null | undefined): value is string {
  return !!value && brandPresets.some((preset) => preset.id === value);
}
